// src/hooks/useBoutique.js
import { useState, useEffect, useCallback } from "react";
import { usePanier } from "../contexts/PanierContext";
import { useFilteredProducts } from "./useFilteredProducts";
import { recupererCatalogueAvecLait } from "../utils/productService";

export const useBoutique = (apiPost, profilPricing) => {
  // 1. GESTION DE LA VUE & ÉTATS
  const [vueActuelle, setVueActuelle] = useState(() => {
    return localStorage.getItem("vueActuelle") || "magasin";
  });

  // ✅ INITIALISATION À NULL : Pour distinguer "en attente" de "vide"
  const [produits, setProduits] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categorieActive, setCategorieActive] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [loading, setLoading] = useState(true);
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState(null);
  const [pagePanier, setPagePanier] = useState(false);
  const [pageCompte, setPageCompte] = useState(false);
  const [storyActive, setStoryActive] = useState(null);

  // ✅ PanierContext (nouvelle API)
  const {
    panier,
    ajouterAuPanier,
    updateQuantite, // ✅ NEW (remplace modifierQuantite)
    viderPanier,
    sousTotal,
  } = usePanier();

  // ✅ Compat: on garde le nom "modifierQuantite" pour tes anciens composants
  const modifierQuantite = updateQuantite;

  /**
   * ============================================================
   * GUIDE — FILTRAGE RÉACTIF
   * ============================================================
   *
   * useFilteredProducts renverra [] si produits est null.
   * On garde la signature existante pour ne pas élargir inutilement
   * l’impact de ce patch.
   */
  const produitsAffiches = useFilteredProducts(
    produits,
    vueActuelle,
    profilPricing
  );

  // Persistance de la vue
  useEffect(() => {
    localStorage.setItem("vueActuelle", vueActuelle);
  }, [vueActuelle]);

  /**
   * ============================================================
   * GUIDE — NORMALISATION DES DONNÉES (DATA LAYER)
   * ============================================================
   *
   * Objectif :
   * - garder la normalisation legacy prix / stock / image
   * - conserver le nouveau champ type_lait
   * - éviter tout impact sur pricing / checkout
   *
   * IMPORTANT :
   * type_lait reste un enrichissement optionnel.
   */
  const normaliserProduit = (p) => {
    const nomFichier = p.image ? p.image.trim() : "";

    /**
     * ✅ Lead fix: normalisation backend -> centimes UI
     * Cas observé: backend renvoie 85000 pour 8.50 CHF (donc ×100 en trop).
     * Stratégie: si la valeur est "grande" et multiple de 100, on divise par 100.
     * Sinon on la considère déjà en centimes.
     */
    const safeCents = (v) => {
      const raw = Number(v);
      if (!Number.isFinite(raw)) return 0;

      const n = Math.round(raw);

      // Heuristique robuste:
      // - 100 CHF en centimes = 10000
      // - tes valeurs sont typiquement >= 10000 et finissent par "00" (×100)
      if (n >= 10000 && n % 100 === 0) return Math.round(n / 100);

      return n;
    };

    return {
      ...p,
      id: parseInt(p.id),
      price: safeCents(p.price),
      old_price: safeCents(p.old_price),
      discountPrice: p.discountPrice ? safeCents(p.discountPrice) : null,
      isOnline: parseInt(p.isOnline) === 1 || p.isOnline === true,
      isDiscount: parseInt(p.isDiscount) === 1 || p.isDiscount === true,
      stock: parseInt(p.stock) || 0,

      /**
       * ========================================================
       * GUIDE — CONSERVATION TYPE DE LAIT
       * ========================================================
       *
       * On garde le champ enrichi tel quel, de façon tolérante.
       * S’il est absent, on pose une chaîne vide.
       */
      type_lait: typeof p.type_lait === "string" ? p.type_lait.trim() : "",

      imageUrl: nomFichier
        ? nomFichier.startsWith("images/")
          ? `https://marchedeluxe.ch/${nomFichier}`
          : `https://marchedeluxe.ch/images/${nomFichier}`
        : "https://marchedeluxe.ch/images/placeholder.png",
    };
  };

  // 3. FETCH API
  const rafraichirTousLesProduits = async () => {
    setLoading(true);

    try {
      /**
       * ========================================================
       * GUIDE — CATALOGUE ENRICHI TYPE DE LAIT
       * ========================================================
       *
       * On remplace ici le flux catalogue classique par le helper
       * centralisé productService.
       *
       * Pourquoi :
       * - récupérer type_lait en boutique
       * - éviter les endpoints relatifs dispersés
       * - rester aligné avec la nouvelle architecture du système lait
       *
       * IMPORTANT :
       * - ce changement ne touche que le chargement catalogue boutique
       * - aucun impact sur l’admin legacy
       * - aucun impact sur checkout / Stripe
       */
      const data = await recupererCatalogueAvecLait();

      if (Array.isArray(data)) {
        setProduits(data.map(normaliserProduit));
      } else {
        setProduits([]);
      }
    } catch (err) {
      console.error("Erreur Lecture Produits:", err);
      setProduits([]); // Évite de rester à null en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  const chargerCategories = async () => {
    setCatLoading(true);
    try {
      const response = await fetch(
        `https://marchedeluxe.ch/services/get_categories.php`
      );
      const result = await response.json();
      if (result.status === "success") setCategories(result.data);
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatLoading(false);
    }
  };

  useEffect(() => {
    rafraichirTousLesProduits();
    chargerCategories();
  }, []);

  const resetVues = useCallback(() => {
    setPagePanier(false);
    setPageCompte(false);
    setStoryActive(null);
    setCategorieActive(null);
    setRecherche("");
  }, []);

  // 4. ACTIONS CRUD
  const ajouterProduit = async (form) => {
    try {
      const payload = {
        ...form,
        isDiscount: form.isDiscount ? 1 : 0,
        isOnline: form.isOnline !== undefined ? (form.isOnline ? 1 : 0) : 1,
      };
      const data = await apiPost(
        "https://marchedeluxe.ch/services/ajouter_produit.php",
        payload
      );
      if (data?.success) {
        await rafraichirTousLesProduits();
        await chargerCategories();
        return data.id;
      }
    } catch (err) {
      console.error("Erreur Ajout:", err);
    }
    return null;
  };

  const mettreAJourProduit = async (id, champ, valeur) => {
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/update_produit_general.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id, champ, valeur }),
        }
      );
      const data = await res.json();
      if (data.success) {
        await rafraichirTousLesProduits();
        return true;
      }
    } catch (err) {
      console.error("Erreur Update Rapide:", err);
    }
    return false;
  };

  const mettreAJourProduitComplet = async (p) => {
    const payload = {
      ...p,
      id: Number(p.id),
      complet: true,
      price: Math.round(p.price),
      old_price: Math.round(p.old_price || 0),
    };
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/update_produit_general.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (data.success) {
        await rafraichirTousLesProduits();
        return true;
      }
    } catch (err) {
      console.error("Erreur Update Complet:", err);
    }
    return false;
  };

  const supprimerProduit = async (id) => {
    if (!window.confirm("Confirmer la suppression ?")) return;
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/supprimer_produit.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id }),
        }
      );
      const data = await res.json();
      if (data.success) setProduits((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Erreur Suppression:", err);
    }
  };

  const uploaderImage = async (id, file) => {
    const fd = new FormData();
    fd.append("image", file);
    fd.append("id", id);
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/upload_image.php",
        {
          method: "POST",
          body: fd,
          credentials: "include",
        }
      );
      const result = await res.json();
      if (result.success) {
        await rafraichirTousLesProduits();
        return true;
      }
    } catch (err) {
      console.error("Erreur Upload:", err);
    }
    return false;
  };

  return {
    vueActuelle,
    setVueActuelle,
    produits,
    produitsAffiches,
    isLoading: loading,
    categories,
    catLoading,
    catError,
    categorieActive,
    setCategorieActive,
    recherche,
    setRecherche,
    panier,
    ajouterAuPanier,
    modifierQuantite, // ✅ compat (alias)
    updateQuantite, // ✅ new API
    viderPanier,
    sousTotal,
    ajouterProduit,
    mettreAJourProduit,
    mettreAJourProduitComplet,
    supprimerProduit,
    uploaderImage,
    rafraichirTousLesProduits,
    pagePanier,
    setPagePanier,
    pageCompte,
    setPageCompte,
    storyActive,
    setStoryActive,
    resetVues,
  };
};
